import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useCTUser } from '@ct/oneid/react';
import { signOutOneId, useAppRole } from '../../lib/auth.ts';

/**
 * User menu — shown in the header when a user is signed in via CT One ID.
 *
 * Identity (name / email / avatar) comes from `useCTUser()`; the atlas role
 * comes from `/api/me` (via `useAppRole`). Sign-out clears the shared One ID
 * session and returns to `/`.
 */
export function UserMenu() {
  const { user, isLoading } = useCTUser();
  const { role } = useAppRole();

  if (isLoading || !user) return null;

  const name = user.displayName || user.primaryContactEmail || 'Account';
  const email = user.primaryContactEmail ?? '';
  const avatarUrl = user.image;
  const initials =
    name
      .split(/\s+/)
      .map((s) => s[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase() || '?';

  function handleSignOut() {
    void signOutOneId();
  }

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-bg-raised text-2xs font-semibold uppercase tracking-wider text-fg-muted ring-1 ring-border-subtle transition-colors duration-quick hover:text-fg focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          aria-label={`Account menu for ${name}`}
        >
          {avatarUrl ? (
            <img src={avatarUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            <span aria-hidden>{initials}</span>
          )}
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={8}
          className="z-dropdown w-64 rounded-md border border-border-subtle bg-bg-raised py-1 shadow-raised data-[state=open]:animate-fade-in"
        >
          <div className="flex items-center gap-3 px-3 py-2.5">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-bg-base text-2xs font-semibold uppercase tracking-wider text-fg-muted ring-1 ring-border-subtle">
              {avatarUrl ? (
                <img src={avatarUrl} alt="" className="h-full w-full object-cover" />
              ) : (
                <span aria-hidden>{initials}</span>
              )}
            </div>
            <div className="grid min-w-0 flex-1 leading-tight">
              <span className="truncate text-sm font-medium text-fg">{name}</span>
              {email ? <span className="truncate text-xs text-fg-subtle">{email}</span> : null}
              <span className="mt-0.5 font-mono text-2xs uppercase tracking-[0.18em] text-fg-subtle">
                {role}
              </span>
            </div>
          </div>

          <DropdownMenu.Separator className="my-1 h-px bg-border-subtle" />

          <DropdownMenu.Item
            onSelect={handleSignOut}
            className="flex cursor-pointer select-none items-center gap-2 px-3 py-2 text-sm text-fg outline-none transition-colors duration-quick data-[highlighted]:bg-bg-base data-[highlighted]:text-fg"
          >
            Sign out
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
